import { Fingerprint, XCircle } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { useTranslations } from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { useSessions } from "../hooks/useSessions";
import type { SessionEntity } from "../types/sessions.types";
import { SettingsSection } from "./SettingsSection";

interface RelativeLabels {
  now: string;
  minutes: (n: number) => string;
  hours: (n: number) => string;
  days: (n: number) => string;
}

function relativeLabel(iso: string, labels: RelativeLabels): string {
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return labels.now;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return labels.minutes(diffMin);
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return labels.hours(diffHours);
  const diffDays = Math.floor(diffHours / 24);
  return labels.days(diffDays);
}

export function DevicesSection() {
  const { t } = useTranslations("settings");
  const { sessions, isLoading, revoke, revokeOthers } = useSessions();
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const [confirmRevokeOthers, setConfirmRevokeOthers] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleRevoke = async (id: string) => {
    setBusy(true);
    try {
      await revoke(id);
      colocaToast.success(t("devices_revoke_success"));
    } catch {
      colocaToast.error(t("devices_revoke_error"));
    } finally {
      setBusy(false);
      setConfirmRevokeId(null);
    }
  };

  const handleRevokeOthers = async () => {
    setBusy(true);
    try {
      const count = await revokeOthers();
      colocaToast.success(t("devices_revoke_others_success", { n: count }));
    } catch {
      colocaToast.error(t("devices_revoke_error"));
    } finally {
      setBusy(false);
      setConfirmRevokeOthers(false);
    }
  };

  const sessionToRevoke = sessions.find((s) => s.id === confirmRevokeId);
  const othersCount = sessions.filter((s) => !s.is_current).length;

  const relativeLabels: RelativeLabels = {
    now: t("devices_last_seen_now"),
    minutes: (n) => t("devices_last_seen_minutes", { n }),
    hours: (n) => t("devices_last_seen_hours", { n }),
    days: (n) => t("devices_last_seen_days", { n }),
  };

  return (
    <SettingsSection
      id="dispositivos"
      eyebrow={t("devices_section_eyebrow")}
      title={t("devices_section_title")}
      sub={t("devices_section_subtitle")}
    >
      <div className="flex flex-col gap-2">
        {isLoading && sessions.length === 0 ? (
          <>
            <ColocaSkeleton className="h-[54px] w-full rounded-md" />
            <ColocaSkeleton className="h-[54px] w-full rounded-md" />
          </>
        ) : (
          sessions.map((s) => (
            <SessionRow
              key={s.id}
              session={s}
              relative={relativeLabel(s.last_seen_at, relativeLabels)}
              currentLabel={t("devices_badge_current")}
              revokeLabel={t("devices_action_revoke")}
              onRevoke={() => setConfirmRevokeId(s.id)}
              disabled={busy}
            />
          ))
        )}
      </div>

      {othersCount > 0 && (
        <div className="mt-4">
          <ColocaButton
            variant="ghost"
            icon={XCircle}
            disabled={busy}
            onClick={() => setConfirmRevokeOthers(true)}
            className="text-destructive hover:bg-destructive/10"
          >
            {t("devices_action_revoke_others")}
          </ColocaButton>
        </div>
      )}

      <ColocaDialog
        open={!!confirmRevokeId}
        onOpenChange={(open) => !open && setConfirmRevokeId(null)}
        title={t("devices_confirm_revoke_title")}
        description={t("devices_confirm_revoke_body", {
          device: sessionToRevoke?.device_label ?? sessionToRevoke?.user_agent ?? "",
        })}
        size="default"
        footer={
          <>
            <ColocaDialogClose
              render={<ColocaButton variant="ghost">{t("devices_confirm_cancel")}</ColocaButton>}
            />
            <ColocaButton
              variant="destructive"
              icon={XCircle}
              loading={busy}
              onClick={() => confirmRevokeId && handleRevoke(confirmRevokeId)}
            >
              {t("devices_confirm_revoke")}
            </ColocaButton>
          </>
        }
      />

      <ColocaDialog
        open={confirmRevokeOthers}
        onOpenChange={(open) => !open && setConfirmRevokeOthers(false)}
        title={t("devices_confirm_revoke_others_title")}
        description={t("devices_confirm_revoke_others_body", { n: othersCount })}
        size="default"
        footer={
          <>
            <ColocaDialogClose
              render={<ColocaButton variant="ghost">{t("devices_confirm_cancel")}</ColocaButton>}
            />
            <ColocaButton variant="destructive" icon={XCircle} loading={busy} onClick={handleRevokeOthers}>
              {t("devices_confirm_revoke_others")}
            </ColocaButton>
          </>
        }
      />
    </SettingsSection>
  );
}

interface SessionRowProps {
  session: SessionEntity;
  relative: string;
  currentLabel: string;
  revokeLabel: string;
  onRevoke: () => void;
  disabled: boolean;
}

function SessionRow({ session, relative, currentLabel, revokeLabel, onRevoke, disabled }: SessionRowProps) {
  return (
    // Wraps: with a real basis on the text, a phone puts "Cerrar sesión" on its own line instead of squeezing
    // the device name beside it.
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card px-3.5 py-3">
      <div className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-md border border-border bg-muted/40 text-muted-foreground">
        <Fingerprint size={15} />
      </div>
      <div className="min-w-0 flex-1 basis-48">
        <div className="flex items-center gap-2 text-[13px] font-medium text-foreground">
          <span className="truncate">{session.device_label ?? session.user_agent ?? "—"}</span>
          {session.is_current && (
            <ColocaBadge variant="success" className="font-mono text-[9.5px]">
              {currentLabel}
            </ColocaBadge>
          )}
        </div>
        <div className="mt-0.5 text-[11.5px] text-muted-foreground">
          {session.ip_address && (
            <>
              <span className="font-mono">{session.ip_address}</span>
              {" · "}
            </>
          )}
          {relative}
        </div>
      </div>
      {!session.is_current && (
        <ColocaButton variant="ghost" size="sm" className="ml-auto" onClick={onRevoke} disabled={disabled}>
          <span className="text-destructive">{revokeLabel}</span>
        </ColocaButton>
      )}
    </div>
  );
}
